import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import R from 'ramda'
import { AgGridReact } from 'ag-grid-react'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { DatePicker, Spin, Button, Menu, Dropdown, Icon } from 'antd'
import AddEventModal from './AddEventModal'
import EventPreviewModal from './EventPreviewModal'
import moment from 'moment'
import { UtilityUnitSelection } from '../../core'
import { Title, CriteriaRow, Content } from 'core/screen-util'
import {
  getUnits,
  getEventTypes,
  getContribOperCondition,
  getApiTargetInfo, // Added
} from '../../core/selectors'
import { getCauseCodeAll } from '../../api/pgads' // Check correct path, likely ../api/pgads or api/pgads

const UnitHourlyPoc = ({
  units,
  eventTypes,
  contribConditions,
  apiTargetInfo,
}) => {
  const [selectedDate, setSelectedDate] = useState(moment())
  const [selectedUtility, setSelectedUtility] = useState(null)
  const [selectedUnit, setSelectedUnit] = useState(null)
  const [rowData, setRowData] = useState([])
  const [loading, setLoading] = useState(false)
  const [causeCodeList, setCauseCodeList] = useState(null) // Lifted state

  // Modal State
  const [modalVisible, setModalVisible] = useState(false)
  const [modalInitialData, setModalInitialData] = useState({})

  // Preview State
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewEvents, setPreviewEvents] = useState([])

  // Logic to map utility+unit to unitCode (id1)
  // Replicating logic from Card97Antd.js
  const unitCodeMap = useMemo(
    () =>
      R.compose(
        R.fromPairs,
        R.map(a => [`${a.utility}${a.unit}`, a.id1])
      )(units || []),
    [units]
  )

  // Fetch Cause Codes once
  useEffect(() => {
    if (!causeCodeList && apiTargetInfo) {
      getCauseCodeAll(apiTargetInfo).fork(
        err => console.error('Failed to fetch cause codes', err),
        list => setCauseCodeList(list)
      )
    }
  }, [apiTargetInfo, causeCodeList])

  // Derived correct code for Cause Code Selector
  const realSelectedUnitCode =
    selectedUtility && selectedUnit
      ? unitCodeMap[`${selectedUtility}${selectedUnit}`]
      : null

  const handleMenuClick = useCallback(
    ({ key }, hour) => {
      if (key === '1') {
        const hourInt = parseInt(hour, 10)
        // Default to Start of the Hour Ending (e.g. HE 6 -> 05:00)
        const initDate = selectedDate
          ? selectedDate
              .clone()
              .hour(hourInt - 1)
              .minute(0)
          : moment()

        // Calculate Unallocated MW for this hour
        const unallocRow = rowData.find(r => r.metric === 'Unallocated Energy')
        const unallocatedMW = unallocRow ? unallocRow[`h${hourInt}`] : 0

        setModalInitialData({
          date: initDate,
          hour: hourInt - 1,
          unallocatedMW,
          existingData: null,
        })
        setModalVisible(true)
      }
    },
    [selectedDate, rowData]
  )

  const handleEditEvent = useCallback((eventData, rowId) => {
    setModalInitialData({
      existingData: eventData,
      rowId: rowId,
    })
    setModalVisible(true)
  }, [])

  // Generate columns
  const columnDefs = useMemo(() => {
    const cols = [
      {
        headerName: 'Hour Ending',
        field: 'metric',
        pinned: 'left',
        width: 250,
        sortable: false,
        filter: false,
        cellStyle: { fontWeight: 'bold' },
        cellRendererFramework: params => {
          if (params.data.isEventRow) {
            const { eventType, comments, causeCode, causeCodeDescription } =
              params.data.eventDetails || {}
            const display = `${eventType} (${causeCode} - ${causeCodeDescription}): ${comments ||
              ''}`
            const truncated =
              display.length > 35 ? display.substring(0, 35) + '...' : display
            return (
              <a
                href="#"
                onClick={e => {
                  e.preventDefault()
                  handleEditEvent(params.data.eventDetails, params.data.metric)
                }}
                style={{ color: '#1890ff', textDecoration: 'underline' }}
                title={display}
              >
                {truncated}
              </a>
            )
          }
          return params.value
        },
      },
    ]

    for (let i = 1; i <= 24; i++) {
      const hour = i.toString().padStart(2, '0')
      cols.push({
        headerName: hour,
        field: `h${i}`,
        width: 55,
        sortable: false,
        // cellStyle removed here, handled by dynamic function below
        editable: params => {
          const metric = params.data.metric
          return (
            metric === 'Dispatch Reduced Load' ||
            metric.startsWith('Event Code') ||
            params.data.isEventRow
          )
        },
        cellClass: params => {
          const metric = params.data.metric
          if (
            metric === 'Dispatch Reduced Load' ||
            metric.startsWith('Event Code') ||
            params.data.isEventRow
          ) {
            return 'editable-cell-style'
          }
          if (metric === 'Unallocated Energy' && params.value !== 0) {
            return 'danger-cell-style'
          }
          if (metric === 'Unit Status') {
            // Classes are static, but we need dynamic style.
            // See cellStyle below.
            return null
          }
          return null
        },
        cellStyle: params => {
          if (params.data.metric === 'Unit Status' && params.data.cellStyles) {
            return {
              ...params.data.cellStyles[params.colDef.field],
              textAlign: 'center',
              padding: 0,
            }
          }
          return { textAlign: 'center', padding: 0 }
        },
        cellRendererFramework: params => {
          if (params.data.metric === 'Actions') {
            const hour = params.colDef.headerName
            const menu = (
              <Menu onClick={e => handleMenuClick(e, hour)}>
                <Menu.Item key="1">Add Event (Hr {hour})</Menu.Item>
                <Menu.Item key="2">View Hour Detail</Menu.Item>
              </Menu>
            )
            return (
              <div style={{ textAlign: 'center' }}>
                <Dropdown overlay={menu} trigger={['click']}>
                  <Button
                    size="small"
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: '#1890ff',
                    }}
                  >
                    <Icon type="plus-circle" />
                  </Button>
                </Dropdown>
              </div>
            )
          }
          return params.value
        },
      })
    }
    return cols
  }, [handleMenuClick, handleEditEvent])

  const calculateUnallocated = items => {
    const ndcRow = items.find(r => r.metric === 'Net Dependable Capacity')
    const netGenRow = items.find(r => r.metric === 'Net Generation')
    const drlRow = items.find(r => r.metric === 'Dispatch Reduced Load')
    const eventCountRowIndex = items.findIndex(r => r.metric === 'Event Count')

    // robust check for event rows
    const eventRows = items.filter(
      r => r.isEventRow || r.metric.startsWith('Event Code')
    )
    const unallocRowIndex = items.findIndex(
      r => r.metric === 'Unallocated Energy'
    )

    if (!ndcRow || !netGenRow || !drlRow || unallocRowIndex === -1) return items

    const newUnallocRow = { ...items[unallocRowIndex] }
    const newEventCountRow =
      eventCountRowIndex !== -1 ? { ...items[eventCountRowIndex] } : null

    for (let i = 1; i <= 24; i++) {
      const hKey = `h${i}`
      const ndc = Number(ndcRow[hKey]) || 0
      const netGen = Number(netGenRow[hKey]) || 0
      const drl = Number(drlRow[hKey]) || 0

      let eventSum = 0
      let eventCount = 0

      eventRows.forEach(row => {
        const val = Number(row[hKey]) || 0
        eventSum += val
        if (val > 0) eventCount++
      })

      newUnallocRow[hKey] = ndc - netGen - drl - eventSum
      if (newEventCountRow) {
        newEventCountRow[hKey] = eventCount
      }
    }

    const newItems = [...items]
    newItems[unallocRowIndex] = newUnallocRow
    if (newEventCountRow && eventCountRowIndex !== -1) {
      newItems[eventCountRowIndex] = newEventCountRow
    }
    return newItems
  }

  // Calculate Unit Status Styles
  const calculateUnitStatus = items => {
    const statusRowIndex = items.findIndex(r => r.metric === 'Unit Status')
    if (statusRowIndex === -1) return items

    const eventRows = items.filter(r => r.isEventRow)
    const newStatusRow = { ...items[statusRowIndex], cellStyles: {} }

    for (let i = 1; i <= 24; i++) {
      // Actually, let's use the logic where H1 = 00:00 - 01:00
      // So H(i) starts at (i-1):00 and ends at i:00
      const hStartVal = i - 1
      const hEndVal = i

      // Default: White
      const styles = { background: 'white' }

      // Check for overlapping events (Outage or RS)
      // We need to support gradients if partial.
      // Priority: Outage (Red) > RS (Gray) > Normal (White)
      // Only considering U1, U2, U3, SF, FO, RS types.

      const relevantEvents = eventRows.filter(r => {
        const type = r.eventDetails?.eventType
        return ['U1', 'U2', 'U3', 'SF', 'FO', 'RS'].includes(type)
      })

      if (relevantEvents.length > 0) {
        // Find if any relevant event covers this hour
        // For simplicity in this POC, we check if the event *exists* in this hour (value > 0)
        // OR checks exact time from start/end.
        // User requested partial coloring: "There could be times that the hour is partially in Full outage"
        // This implies we must look at `eventDetails.startTime`.
        // What about End Time? We calculate it as last non-zero.

        const redSegments = []
        const graySegments = []

        relevantEvents.forEach(ev => {
          const type = ev.eventDetails.eventType
          const startTime = moment(ev.eventDetails.startTime)
          // End Time fallback logic from Preview:
          let endIdx = -1
          for (let k = 24; k >= 1; k--) {
            if ((Number(ev[`h${k}`]) || 0) > 0) {
              endIdx = k
              break
            }
          }
          if (endIdx === -1) endIdx = 24 // fallback or startIdx?

          // Calculate effective minutes in this hour (hStartVal to hEndVal)
          // Hour span: [hStartVal:00, hEndVal:00)
          // Compare startTime and endTime against this window.
          // Note: This assumes all events belong to selectedDate. Cross-day not fully supported yet.

          // Event Start Hour (float)
          const evStartHour = startTime.hour() + startTime.minute() / 60

          // Event End Hour (float) -> endIdx is the *column* index.
          // If value in h1 (00-01) is set, it ends at 01:00? Or starts at 00:00?
          // "End time is determined by the hour where last non-zero value is entered"
          // If h1 puts 50, it means the event is active during 00-01.
          // So End Time is effectively `endIdx`:00.
          const evEndHour = endIdx

          // Intersection of [evStartHour, evEndHour] and [hStartVal, hEndVal]
          const startMax = Math.max(evStartHour, hStartVal)
          const endMin = Math.min(evEndHour, hEndVal)

          if (startMax < endMin) {
            // We have overlap
            const startPct = (startMax - hStartVal) * 100 // 0 to 100
            const endPct = (endMin - hStartVal) * 100 // 0 to 100

            if (['U1', 'U2', 'U3', 'SF', 'FO'].includes(type)) {
              redSegments.push({ start: startPct, end: endPct })
            } else if (type === 'RS') {
              graySegments.push({ start: startPct, end: endPct })
            }
          }
        })

        // Construct Gradient
        // Simplification: If multiple events align, Red wins.
        // We'll create a gradient string.
        if (redSegments.length > 0 || graySegments.length > 0) {
          // Sort segments?
          // Let's assume one event dominants or they don't overlap much.
          // If full overlap: background: red.
          // Gradient syntax: linear-gradient(to right, color start%, color end%, ...)

          // Merging is complex. Let's try simple "Max coverage" or just first one found.
          // Requirement: "cell is half colored"

          // Strategy: Build a stop list for the gradient. 0% to 100%.
          // We paint Red first, then Gray, then White.

          const stops = []
          // Add Red stops
          redSegments.forEach(seg => {
            stops.push({ pct: seg.start, color: 'red', type: 'start' })
            stops.push({ pct: seg.end, color: 'red', type: 'end' })
          })
          // Add Gray stops
          graySegments.forEach(seg => {
            stops.push({ pct: seg.start, color: 'gray', type: 'start' })
            stops.push({ pct: seg.end, color: 'gray', type: 'end' })
          })

          if (stops.length > 0) {
            // If full 0-100 red:
            const isFullRed = redSegments.some(
              s => s.start <= 0 && s.end >= 100
            )
            const isFullGray = graySegments.some(
              s => s.start <= 0 && s.end >= 100
            )

            if (isFullRed) styles.background = '#ff4d4f'
            // AntD Red
            else if (isFullGray) styles.background = '#bfbfbf'
            // AntD Gray
            else {
              // Partial
              // Construct linear gradient
              // Example: 0-50% Red, 50-100% White -> red 0%, red 50%, white 50%, white 100%
              // We need valid CSS syntax.
              // Let's take the first significant segment.
              // If Red segment 30-70: white 0%, white 30%, red 30%, red 70%, white 70%

              let gradient = 'linear-gradient(90deg'

              // Combine and sort stops isn't enough, we need to flatten logic.
              // Let's just Loop 0 to 100 in steps? No, CSS is exact.

              // Let's look at the FIRST Red segment, if exists.
              if (redSegments.length > 0) {
                const seg = redSegments[0]
                gradient += `, white 0%, white ${seg.start}%, #ff4d4f ${seg.start}%, #ff4d4f ${seg.end}%, white ${seg.end}%, white 100%`
              } else if (graySegments.length > 0) {
                const seg = graySegments[0]
                gradient += `, white 0%, white ${seg.start}%, #bfbfbf ${seg.start}%, #bfbfbf ${seg.end}%, white ${seg.end}%, white 100%`
              }

              gradient += ')'
              styles.background = gradient
            }

            // Text color white if background is dark
            styles.color = isFullRed || isFullGray ? 'white' : 'black'
          }
        }
      }

      newStatusRow.cellStyles[`h${i}`] = styles
    }

    const newItems = [...items]
    newItems[statusRowIndex] = newStatusRow
    return newItems
  }

  const handleSaveEvent = eventData => {
    // Check if we are editing an existing row
    const isEdit = modalInitialData.rowId

    if (isEdit) {
      setRowData(prevData => {
        const newData = [...prevData]
        const rowIndex = newData.findIndex(r => r.metric === isEdit)
        if (rowIndex !== -1) {
          // Update the existing row's event details
          // We preserve the hour values (h1..h24) but update the metadata
          newData[rowIndex] = {
            ...newData[rowIndex],
            eventDetails: eventData,
          }
        }
        return calculateUnitStatus(calculateUnallocated(newData)) // Recalculate just in case
      })
    } else {
      // Create NEW event row
      const newEventRow = {
        metric: `Event-${Date.now()}`, // Unique ID
        isEventRow: true,
        eventDetails: eventData,
      }
      for (let i = 1; i <= 24; i++) {
        newEventRow[`h${i}`] = 0
      }
      if (modalInitialData.hour) {
        const hIdx = parseInt(modalInitialData.hour, 10)
        newEventRow[`h${hIdx}`] = eventData.mwAmount || 50
      }
      setRowData(prevData => {
        const newData = [...prevData]
        const unallocIdx = newData.findIndex(
          r => r.metric === 'Unallocated Energy'
        )
        if (unallocIdx !== -1) {
          newData.splice(unallocIdx, 0, newEventRow)
        } else {
          newData.push(newEventRow)
        }
        return calculateUnitStatus(calculateUnallocated(newData))
      })
    }
    setModalVisible(false)
  }

  const handleRetrieve = () => {
    if (!selectedUnit) return
    setLoading(true)
    setTimeout(() => {
      const rows = []
      const createRow = (name, valFn) => {
        const row = { metric: name }
        for (let i = 1; i <= 24; i++) {
          row[`h${i}`] = valFn(i)
        }
        return row
      }

      rows.push(createRow('Unit Status', () => '')) // Status Row
      rows.push(createRow('Event Count', () => 0)) // Init to 0, calc will update
      rows.push(createRow('Net Dependable Capacity', () => 600))
      const grossRow = { metric: 'Gross Generation' }
      const stationRow = { metric: 'Station Service' }
      const netRow = { metric: 'Net Generation' }

      for (let i = 1; i <= 24; i++) {
        const station = Math.floor(Math.random() * 50) + 10
        const gross = Math.floor(Math.random() * 400) + 100 + station
        grossRow[`h${i}`] = gross
        stationRow[`h${i}`] = station
        netRow[`h${i}`] = gross - station
      }
      rows.push(grossRow)
      rows.push(stationRow)
      rows.push(netRow)
      rows.push(createRow('Dispatch Reduced Load', () => 0))
      rows.push({ metric: 'Actions' })
      // Removed mock event row
      rows.push(createRow('Unallocated Energy', () => 0))

      const calculatedRows = calculateUnallocated(rows)
      const stautsRows = calculateUnitStatus(calculatedRows)
      setRowData(stautsRows)
      setLoading(false)
    }, 500)
  }

  const handlePreviewEvents = () => {
    const eventRows = rowData.filter(r => r.isEventRow)
    const previews = eventRows.map(row => {
      const {
        eventType,
        causeCode,
        causeCodeDescription,
        comments,
        startTime,
      } = row.eventDetails || {}

      // Start Time Logic - Derived from Data
      // User Request: "when it has mw values in hour ending 06 it should be starting at 05:00"
      // Find First Non-Zero Hour
      let startIdx = -1
      for (let i = 1; i <= 24; i++) {
        const val = Number(row[`h${i}`]) || 0
        if (val > 0) {
          startIdx = i
          break
        }
      }

      // Fallback if no data: Use stored start time or default to 1 (00:00)
      if (startIdx === -1) {
        const storedStartHour = startTime ? moment(startTime).hour() : 0
        startIdx = storedStartHour === 0 ? 24 : storedStartHour // simple fallback
      }

      // Calculate Start Moment based on startIdx (Hour Ending)
      // HE 1 (Index 1) starts at 00:00. HE 6 (Index 6) starts at 05:00.
      // Formula: Hour = Index - 1
      const startMoment = selectedDate
        .clone()
        .hour(startIdx - 1)
        .minute(0)
        .second(0)

      // End Time Logic (Last non-zero hour)
      let endIdx = -1
      for (let i = 24; i >= 1; i--) {
        const val = Number(row[`h${i}`]) || 0
        if (val > 0) {
          endIdx = i
          break
        }
      }

      // If no values, default to Start
      if (endIdx === -1) endIdx = startIdx

      // Calculate End Time based on Date + Hour
      // We assume same day for simplicity unless it wraps (not handled here)
      const endMoment = startMoment
        .clone()
        .hour(endIdx)
        .minute(0)

      // Calculate Avg Derate
      // Range: [startIdx, endIdx] (inclusive of end, but start might be later if user entered data BEFORE start time?
      // Logic: "end time is deteremined by the hour where last non-zero value is entered"
      // User says "there could be intermitted 0 in bettween we need to consider those hours"
      // So Range is effectively Start..End.
      // But what is Start? Is it strictly `startTime`'s hour? Or first non-zero?
      // "The events needs to have an additiaonl end time field. the end time is deteremined by the hour where last non-zero value is entered"
      // Implicitly Start Time is fixed.
      // We need to support startIdx > endIdx (if user entered nothing?) -> Duration 0?

      // Let's rely on data: strictly use [startIdx ... endIdx]
      // If startIdx > endIdx (e.g. started hr 5, data only in hr 3), swap or just 0?
      // Let's assume valid data entry: data starts >= startIdx.
      // Actually, if user puts data in h1 but start time is h5, what happens?
      // "End time is last non-zero".
      // Let's assume Start is fixed from Event Details. End is calculated.

      let sum = 0
      let count = 0

      // Handle potential wrap or bad data: if endIdx < startIdx, maybe just use EndIdx as single point?
      // Or maybe startIdx is merely metadata and data implies reality.
      // Let's strict: Iterate from startIdx to endIdx.
      if (endIdx >= startIdx) {
        for (let i = startIdx; i <= endIdx; i++) {
          sum += Number(row[`h${i}`]) || 0
          count++
        }
      } else {
        // Fallback: just use endIdx value? Or 0.
        // If data is in h3 but start is h5.
        // User might have edited start time?
        // Let's calculate avg over ALL non-zero range?
        // User said: "average value over the duration of the event"
        // Duration = Start -> End.
        // If End < Start, it's invalid duration.
        // Let's just sum all? No, "intermittent 0 in between".
        // Let's stick to Start..End. If End < Start, return 0.
      }

      const avg = count > 0 ? sum / count : 0

      return {
        eventType,
        causeCode,
        causeCodeDescription,
        comments,
        startTime: startMoment.toDate(),
        endTime: endMoment.toDate(),
        avgDerate: avg,
      }
    })

    setPreviewEvents(previews)
    setPreviewVisible(true)
  }

  useEffect(() => {
    setRowData([])
  }, [selectedUtility, selectedUnit, selectedDate])

  const onCellValueChanged = params => {
    if (
      params.data.metric === 'Dispatch Reduced Load' ||
      params.data.metric.startsWith('Event Code') ||
      params.data.isEventRow
    ) {
      const updatedRowData = []
      params.api.forEachNode(node => updatedRowData.push(node.data))
      const newRowData = calculateUnallocated(updatedRowData)
      const statusRows = calculateUnitStatus(newRowData)
      setRowData(statusRows)
    }
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CriteriaRow>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '200px 250px 350px 100px 150px',
            width: '100%',
            gap: '10px',
            alignItems: 'center',
          }}
        >
          <Title>Unit Hourly POC</Title>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>Date:</span>
            <DatePicker
              value={selectedDate}
              onChange={setSelectedDate}
              allowClear={false}
              format="MMM/DD/YYYY"
              style={{ flex: 1 }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>Unit:</span>
            <div style={{ flex: 1 }}>
              <UtilityUnitSelection
                utility={selectedUtility}
                unit={selectedUnit}
                onUnitChange={val => {
                  if (val && val.length === 2) {
                    setSelectedUtility(val[0])
                    setSelectedUnit(val[1])
                  }
                }}
              />
            </div>
          </div>

          <Button
            type="primary"
            icon="database"
            onClick={handleRetrieve}
            disabled={!selectedUnit}
          >
            Retrieve
          </Button>

          <Button
            icon="eye"
            onClick={handlePreviewEvents}
            disabled={!selectedUnit}
          >
            Preview Events
          </Button>
        </div>
      </CriteriaRow>

      <Content style={{ flex: 1, padding: '10px' }}>
        {loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <Spin size="large" />
          </div>
        ) : (
          <div
            className="ag-theme-alpine"
            style={{ height: '80vh', width: '100%' }}
          >
            <AgGridReact
              columnDefs={columnDefs}
              rowData={rowData}
              defaultColDef={{
                resizable: true,
              }}
              onCellValueChanged={onCellValueChanged}
              suppressMovableColumns
              getRowId={params => params.data.metric}
            />
          </div>
        )}
      </Content>
      <style>
        {`
            .editable-cell-style {
                background-color: #fffde7; /* Light yellow for editable cells */
            }
            .danger-cell-style {
                background-color: #ffcccb; /* Light red for danger */
                font-weight: bold;
            }
            /* Center align editing input */
            .ag-theme-alpine .ag-cell-inline-editing,
            .ag-theme-alpine .ag-cell input {
                text-align: center;
            }
        `}
      </style>
      {modalVisible && (
        <AddEventModal
          visible={modalVisible}
          onCancel={() => setModalVisible(false)}
          onSave={handleSaveEvent}
          initialDate={modalInitialData.date}
          initialHour={modalInitialData.hour}
          selectedUnit={realSelectedUnitCode}
          editData={modalInitialData.existingData}
          eventTypes={eventTypes}
          contribConditions={contribConditions}
          causeCodeList={causeCodeList}
          unallocatedMW={modalInitialData.unallocatedMW}
        />
      )}
      <EventPreviewModal
        visible={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        events={previewEvents}
      />
    </div>
  )
}

const mapStateToProps = state => ({
  units: getUnits(state),
  eventTypes: getEventTypes(state),
  contribConditions: getContribOperCondition(state),
  apiTargetInfo: getApiTargetInfo(state),
})

export default connect(mapStateToProps)(UnitHourlyPoc)
